import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import SiteNav from './marketing/SiteNav';
import SiteFooter from './marketing/SiteFooter';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { useApp } from '../context/AppContext';
import { api } from '../api';

const SIZES = ['Studio', '1-bed flat', '2-bed flat', '3-bed flat', '2-bed house', '3-bed house', '4-bed house', '5+ bed house', 'Commercial'];
const STEPS = ['Service', 'Property', 'Size', 'Date', 'Pricing', 'Confirm'];
const UK_PC = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;

const Booking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, quickLogin } = useApp();
  const [options, setOptions] = useState([]);
  const [step, setStep] = useState(0);
  const [created, setCreated] = useState(null);
  const preOption = new URLSearchParams(location.search).get('option');
  const [form, setForm] = useState({ optionId: preOption || '', address: '', postcode: '', size: '', date: '', notes: '', paymentChoice: 'fixed' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/services').then((r) => {
      const all = r.data.flatMap((p) => p.options.map((o) => ({ ...o, pillarName: p.name })));
      setOptions(all);
      if (preOption && all.find((o) => o.id === preOption)) setStep(1);
    });
  }, [preOption]);

  const option = options.find((o) => o.id === form.optionId);
  const pcValid = UK_PC.test(form.postcode.trim());

  const canNext = () => {
    if (step === 0) return !!form.optionId;
    if (step === 1) return form.address && pcValid;
    if (step === 2) return !!form.size;
    if (step === 3) return !!form.date;
    return true;
  };

  const confirm = async () => {
    try {
      let u = user;
      if (!u || u.role === 'contractor') u = await quickLogin('manager');
      const choice = option?.price ? form.paymentChoice : 'quote';
      const r = await api.post('/bookings', {
        optionId: form.optionId, address: form.address, postcode: form.postcode.toUpperCase(),
        size: form.size, scheduledDate: form.date, notes: form.notes, paymentChoice: choice,
      });
      setCreated(r.data);
      toast({ title: 'Booking confirmed', description: `${r.data.service_name} · ${r.data.id}` });
    } catch (e) {
      toast({ title: 'Booking failed', description: e?.response?.data?.detail || 'Please try again.' });
    }
  };

  if (created) {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <div className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-6"><Icons.CheckCircle2 className="h-8 w-8" /></div>
          <h1 className="font-display font-extrabold text-3xl text-[#102d55]">Booking confirmed</h1>
          <p className="text-muted-foreground mt-3">Job <span className="font-semibold text-foreground">{created.id}</span> created and {created.status === 'broadcasted' ? 'broadcast to matching contractors' : created.status}.</p>
          <div className="mt-8 bg-white border border-border rounded-2xl p-6 text-left divide-y divide-border">
            <Row label="Service" value={created.service_name} />
            <Row label="Property" value={`${created.property_address}, ${created.postcode}`} />
            <Row label="Size" value={created.property_size} />
            <Row label="Date" value={created.scheduled_date} />
            <Row label="Payment" value={created.payment_status} />
            <Row label="Status" value={created.status} last />
          </div>
          <div className="mt-8 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>Back to home</Button>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#102d55] hover:bg-[#0b2244]">Track in dashboard <Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">
        <h1 className="font-display font-extrabold text-3xl text-[#102d55]">Book a service</h1>
        <div className="flex items-center mt-8 mb-10 overflow-x-auto">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ${i <= step ? 'bg-[#102d55] text-white' : 'bg-secondary text-slate-400'}`}>{i < step ? <Icons.Check className="h-4 w-4" /> : i + 1}</span>
                <span className={`text-sm font-medium hidden sm:block ${i <= step ? 'text-[#102d55]' : 'text-slate-400'}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 min-w-[12px] ${i < step ? 'bg-[#102d55]' : 'bg-border'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white border border-border rounded-2xl p-6 sm:p-8">
          {step === 0 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Select a service</h2>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {options.map((o) => (
                  <button key={o.id} onClick={() => set('optionId', o.id)} className={`text-left p-3.5 rounded-xl border transition-colors ${form.optionId === o.id ? 'border-[#102d55] ring-1 ring-[#102d55] bg-secondary/60' : 'border-border hover:border-[#1c63c4]'}`}>
                    <div className="flex items-center justify-between"><span className="font-medium text-sm text-[#102d55]">{o.name}</span><span className="text-sm font-semibold text-slate-600">{o.price ? `£${o.price}` : 'Quote'}</span></div>
                    <span className="text-xs text-muted-foreground">{o.pillarName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Property details</h2>
              <div className="space-y-4">
                <div><Label>Property address *</Label><Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="14 Maple Court, Manchester" className="mt-1.5" /></div>
                <div><Label>Postcode *</Label><Input value={form.postcode} onChange={(e) => set('postcode', e.target.value)} placeholder="M14 5TR" className="mt-1.5" />
                  {form.postcode && !pcValid && <p className="text-xs text-destructive mt-1">Enter a valid UK postcode.</p>}</div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Property size</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {SIZES.map((s) => (
                  <button key={s} onClick={() => set('size', s)} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${form.size === s ? 'border-[#102d55] ring-1 ring-[#102d55] bg-secondary/60 text-[#102d55]' : 'border-border hover:border-[#1c63c4] text-slate-600'}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Choose a date</h2>
              <div><Label>Preferred date *</Label><Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="mt-1.5" /></div>
              <div className="mt-4"><Label>Notes (access, parking, keys)</Label><Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className="mt-1.5" /></div>
            </div>
          )}
          {step === 4 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Pricing</h2>
              {option?.price ? (
                <div className="space-y-3">
                  {[{ k: 'fixed', t: `Pay fixed price now — £${option.price}`, d: 'Payment confirmed instantly (simulated), job dispatched to contractors.' }, { k: 'quote', t: 'Request a quote instead', d: 'We confirm pricing before assignment.' }].map((c) => (
                    <button key={c.k} onClick={() => set('paymentChoice', c.k)} className={`w-full text-left p-4 rounded-xl border transition-colors ${form.paymentChoice === c.k ? 'border-[#102d55] ring-1 ring-[#102d55]' : 'border-border hover:border-[#1c63c4]'}`}>
                      <div className="font-medium text-[#102d55]">{c.t}</div><div className="text-sm text-muted-foreground">{c.d}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl bg-[#eaf1fb] border border-border p-4 text-[#1c63c4] font-medium flex items-center gap-2"><Icons.Calculator className="h-5 w-5" /> This service is quote-based. We'll confirm pricing before assigning a contractor.</div>
              )}
            </div>
          )}
          {step === 5 && (
            <div>
              <h2 className="font-display font-semibold text-lg text-[#102d55] mb-4">Review your booking</h2>
              <div className="divide-y divide-border">
                <Row label="Service" value={`${option?.name} (${option?.pillarName})`} />
                <Row label="Property" value={`${form.address}, ${form.postcode.toUpperCase()}`} />
                <Row label="Size" value={form.size} />
                <Row label="Date" value={form.date} />
                <Row label="Pricing" value={option?.price ? (form.paymentChoice === 'fixed' ? `£${option.price} (pay now)` : 'Quote requested') : 'Quote on request'} last />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="ghost" onClick={() => (step === 0 ? navigate('/services') : setStep(step - 1))}><Icons.ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
            {step < 5 ? <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="bg-[#102d55] hover:bg-[#0b2244]">Continue <Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
              : <Button onClick={confirm} className="bg-[#102d55] hover:bg-[#0b2244]">Confirm booking <Icons.Check className="ml-2 h-4 w-4" /></Button>}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

const Row = ({ label, value, last }) => (
  <div className="flex items-start justify-between gap-4 py-3"><span className="text-sm text-muted-foreground">{label}</span><span className="text-sm font-medium text-[#102d55] text-right capitalize">{value}</span></div>
);

export default Booking;
